/**
 * Simple nav bar component
 *
 * Copyright (C) 2022A Noom, Inc.
 * @author nikola
 */

import React from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  CompassColor,
  Heading,
  Size,
  Spacing,
  Variant,
  VariantColor,
} from "@noom/wax-component-library";
import { useNavigate } from "react-router-dom";

import { ToggledNavLink } from "@modules/layout/components/ToggledNavLink";

import { useDesktopView } from "../../../hooks/useDesktopView";
import { usePatient } from "../../../hooks/usePatient";
import { isSteadyPatient } from "../../../common/utils";

const MobileNavBar = () => {
  const patient = usePatient();
  const navigate = useNavigate();

  const handleLogOutClick = () => {
    navigate("/logout");
  };

  return (
    <Box
      backgroundColor={CompassColor.white}
      zIndex="sticky"
      alignItems="left"
      justifyContent="flex-start"
    >
      <Box padding="20px">
        <Heading>
          <img
            src="/assets/img/noom-med-logo.png"
            alt="noom"
            width="150px"
          />{" "}
        </Heading>
      </Box>
      {/* Logged in tab items are below */}
      {patient && (
        <Accordion minWidth="100%" margin={0} allowToggle={true}>
          <AccordionItem minHeight="0">
            <h2>
              <AccordionButton>
                Menu
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel>
              <ToggledNavLink
                text="Past Visits"
                route="/visits/past"
                data-cy="past-visits-nav"
              />
            </AccordionPanel>
            <AccordionPanel>
              <ToggledNavLink
                text="Upcoming Visits"
                route="/visits/upcoming"
                data-cy="upcoming-visits-nav"
              />
            </AccordionPanel>
            {isSteadyPatient(patient) && (
              <AccordionPanel>
                <ToggledNavLink
                  text="Labs"
                  route="/documents"
                  data-cy="document-tab-nav"
                />
              </AccordionPanel>
            )}
            <AccordionPanel>
              <Button
                variant={Variant.outline}
                variantColor={VariantColor.secondary}
                color={CompassColor.black}
                textTransform="uppercase"
                onClick={handleLogOutClick}
                size={Size.xs}
                fontWeight="normal"
                marginRight={Spacing[4]}
              >
                Logout
              </Button>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      )}
    </Box>
  );
};

const DesktopNavBar = () => {
  const patient = usePatient();
  const navigate = useNavigate();

  const handleLogOutClick = () => {
    navigate("/logout");
  };

  return (
    <Box
      display="flex"
      height="80px"
      maxW="1440px"
      margin="0px auto"
      width="100%"
      backgroundColor={CompassColor.white}
      zIndex="sticky"
      alignItems="center"
      justifyContent="flex-end"
    >
      <Box
        justifySelf="start"
        marginRight="auto"
        marginLeft={Spacing[16]}
        marginTop={Spacing[2]}
      >
        <Heading>
          <img
            src="/assets/img/noom-med-logo.png"
            alt="noom"
            width="150px"
          />{" "}
        </Heading>
      </Box>
      {/* Logged in tab items are below */}
      {patient && (
        <>
          <ToggledNavLink
            text="Visits"
            route="/visits/past"
            useTextForIsActive
            data-cy="visits-tab-nav"
          />
          {isSteadyPatient(patient) && (
            <ToggledNavLink
              text="Labs"
              route="/documents"
              data-cy="documents-tab-nav"
            />
          )}
          <Button
            variant={Variant.outline}
            variantColor={VariantColor.secondary}
            color={CompassColor.black}
            textTransform="uppercase"
            onClick={handleLogOutClick}
            size={Size.md}
            fontWeight="normal"
            marginRight={Spacing[4]}
            data-cy="logout-button"
          >
            Logout
          </Button>
        </>
      )}
    </Box>
  );
};

const NavBar = () => {
  const desktopViewActive = useDesktopView();
  if (desktopViewActive) {
    return <DesktopNavBar />;
  }
  return <MobileNavBar />;
};

export default NavBar;
